import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { fileUrl } from "../lib/api";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Brand = ({ dark, large = false }) => {
  const { settings } = useSite();
  const { t, i18n } = useTranslation();
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
      <img src={settings.logo_image_id ? fileUrl(settings.logo_image_id) : "/logo-full.webp"} alt={t("brand.name")} className={`${large ? "h-32 sm:h-36" : "h-14 sm:h-[4.5rem]"} w-auto object-contain transition-transform duration-500 group-hover:-translate-y-0.5 ${dark ? "drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]" : ""}`} data-testid="brand-logo" />
      <span className={`font-heading text-lg sm:text-xl tracking-wide leading-tight ${i18n.language === "it" ? "sr-only" : "hidden md:inline"}`} data-testid="site-name">{t("brand.name")}</span>
    </Link>
  );
};

export const Header = ({ overlay = false }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const dark = overlay && !scrolled;
  const shell = dark ? "text-[var(--cream)] bg-gradient-to-b from-[rgba(18,14,10,0.55)] to-transparent [text-shadow:0_1px_10px_rgba(0,0,0,0.45)]" : "glass-light text-[hsl(var(--foreground))] shadow-[0_8px_30px_-20px_rgba(0,0,0,0.4)]";
  const links = [
    { to: "/explore", label: t("nav.explore"), id: "nav-explore" },
    { to: "/memorials", label: t("nav.memorials"), id: "nav-memorials" },
    { to: "/contact", label: t("nav.contact"), id: "nav-contact" },
  ];
  return (
    <header className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${shell}`} data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 btn-primary">{t("nav.skip")}</a>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Brand dark={dark} />
        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold" aria-label="main">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.id} className={({ isActive }) => `relative py-1 transition-opacity hover:opacity-100 ${isActive ? "opacity-100" : dark ? "opacity-95" : "opacity-75"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant={dark ? "dark" : "light"} />
          <Link to="/create" className={`hidden sm:inline-flex ${dark ? "btn-glass" : "btn-primary"} !py-2.5 !px-5`} data-testid="header-create-btn">
            {t("nav.create")}
          </Link>
          <button className="md:hidden p-2" onClick={() => setOpen((v) => !v)} aria-label={t("nav.menu")} aria-expanded={open} data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="md:hidden glass-light text-[hsl(var(--foreground))] px-6 pb-6 pt-2 flex flex-col gap-4 text-base font-semibold" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`${l.id}-mobile`}>{l.label}</Link>
          ))}
          <Link to="/create" className="btn-primary" onClick={() => setOpen(false)} data-testid="mobile-create-btn">{t("nav.create")}</Link>
        </nav>
      )}
    </header>
  );
};
